import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { getGlobalDataStore } from "@/lib/mock-data/global-data-store";

export interface UnallocatedPaymentsSummaryApi {
  unallocatedCount: number;
  totalOutstandingAmount: number;
}

/**
 * Hook to fetch unallocated payments summary.
 * In live mode, calls GET /api/v1/receipts/unallocated-summary.
 * In demo mode, returns data derived from the global data store.
 * @param creditControllerId — when provided (live mode only), passes `?creditControllerId=<id>` so the
 *   backend returns counts scoped to customers assigned to that credit controller. Pass `undefined` for all.
 */
export function useUnallocatedPaymentsSummary(creditControllerId?: string) {
  const { apiGet } = useApi();

  const store = getGlobalDataStore();
  const unallocatedReceipts = store.getUnallocatedReceipts();
  const demoData: UnallocatedPaymentsSummaryApi = {
    unallocatedCount: unallocatedReceipts.length,
    totalOutstandingAmount: unallocatedReceipts.reduce(
      (sum, r) => sum + r.unappliedAmount,
      0
    ),
  };

  const params: Record<string, string | number | boolean | undefined> | undefined =
    creditControllerId ? { creditControllerId } : undefined;

  return useModeAwareQuery<UnallocatedPaymentsSummaryApi, Error>(
    ["unallocated-payments-summary", creditControllerId ?? "everyone"],
    async ({ signal }) => {
      return apiGet<UnallocatedPaymentsSummaryApi>(
        "/api/v1/receipts/unallocated-summary",
        params,
        signal
      );
    },
    demoData,
    { staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: false }
  );
}
